import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMeetings, createMeeting, updateMeeting, deleteMeeting } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, CalendarDaysIcon, LinkIcon, TrashIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

export default function MeetingsPage() {
  const { user, isFellow, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [notesModal, setNotesModal] = useState(null);
  const [form, setForm] = useState({ title: '', meeting_date: '', duration_minutes: 60, zoom_link: '', venue: '', agenda: '' });
  const [notesForm, setNotesForm] = useState({ notes: '', action_points: '', attendance: '', status: 'completed' });

  const { data, isLoading } = useQuery({
    queryKey: ['meetings', filter],
    queryFn: () => getMeetings({ status: filter || undefined }),
  });

  const createMut = useMutation({
    mutationFn: createMeeting,
    onSuccess: () => { qc.invalidateQueries(['meetings']); toast.success('Meeting created!'); setShowModal(false); },
    onError: () => toast.error('Failed to create meeting'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateMeeting(id, data),
    onSuccess: () => { qc.invalidateQueries(['meetings']); toast.success('Meeting updated!'); setNotesModal(null); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteMeeting,
    onSuccess: () => { qc.invalidateQueries(['meetings']); toast.success('Deleted'); },
  });

  const meetings = data?.data || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Meetings</h1>
          <p className="text-gray-500 text-sm">Schedule and track fellowship meetings</p>
        </div>
        {isFellow && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> New Meeting
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {['', 'scheduled', 'completed', 'cancelled'].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === s ? 'bg-primary-600 text-white' : 'bg-surface-100 text-gray-400 hover:text-white'}`}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : meetings.length === 0 ? (
        <div className="card text-center py-12">
          <CalendarDaysIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No meetings found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => (
            <div key={m.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{m.title}</span>
                    <span className={`badge ${m.status === 'scheduled' ? 'bg-purple-900/50 text-purple-300' : m.status === 'completed' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>{m.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {format(new Date(m.meeting_date), 'dd MMM yyyy, HH:mm')} · {m.duration_minutes}min
                    {m.venue && ` · ${m.venue}`}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">Organizer: {m.organizer?.full_name}</div>
                  {m.agenda && <div className="mt-2 text-xs text-gray-400 bg-surface-100 rounded-lg p-2 whitespace-pre-wrap">{m.agenda}</div>}
                  {m.notes && <div className="mt-2 text-xs text-gray-400"><span className="text-gray-600 font-medium">Notes: </span>{m.notes}</div>}
                  {m.action_points && <div className="mt-1 text-xs text-amber-400"><span className="text-gray-600 font-medium">Action Points: </span>{m.action_points}</div>}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0 items-end">
                  {m.zoom_link && (
                    <a href={m.zoom_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary-400 hover:underline">
                      <LinkIcon className="w-3.5 h-3.5" /> Join
                    </a>
                  )}
                  {(m.organizer?.id === user?.id || isAdmin) && m.status === 'scheduled' && (
                    <button onClick={() => { setNotesModal(m); setNotesForm({ notes: '', action_points: '', attendance: '', status: 'completed' }); }}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                      <ClipboardDocumentListIcon className="w-3.5 h-3.5" /> Add Notes
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => { if (window.confirm('Delete this meeting?')) deleteMut.mutate(m.id); }} className="text-red-400 hover:text-red-300">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Create Meeting</h2>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate({ ...form, meeting_date: new Date(form.meeting_date).toISOString() }); }} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Date & Time *</label>
                <input type="datetime-local" className="input" required value={form.meeting_date} onChange={(e) => setForm(f => ({ ...f, meeting_date: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Duration (minutes)</label>
                <input type="number" className="input" value={form.duration_minutes} onChange={(e) => setForm(f => ({ ...f, duration_minutes: +e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Zoom/Teams Link</label>
                <input className="input" value={form.zoom_link} onChange={(e) => setForm(f => ({ ...f, zoom_link: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Venue</label>
                <input className="input" value={form.venue} onChange={(e) => setForm(f => ({ ...f, venue: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Agenda</label>
                <textarea className="input resize-none" rows={3} value={form.agenda} onChange={(e) => setForm(f => ({ ...f, agenda: e.target.value }))} /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>
                  {createMut.isPending ? 'Creating...' : 'Create Meeting'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Notes Modal */}
      {notesModal && (
        <Modal onClose={() => setNotesModal(null)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-1">Add Meeting Notes</h2>
            <p className="text-gray-500 text-xs mb-4">{notesModal.title}</p>
            <form onSubmit={(e) => { e.preventDefault(); updateMut.mutate({ id: notesModal.id, data: notesForm }); }} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Meeting Notes</label>
                <textarea className="input resize-none" rows={3} value={notesForm.notes} onChange={(e) => setNotesForm(f => ({ ...f, notes: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Action Points</label>
                <textarea className="input resize-none" rows={2} value={notesForm.action_points} onChange={(e) => setNotesForm(f => ({ ...f, action_points: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Attendance</label>
                <input className="input" placeholder="Names of attendees..." value={notesForm.attendance} onChange={(e) => setNotesForm(f => ({ ...f, attendance: e.target.value }))} /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={updateMut.isPending}>Save Notes</button>
                <button type="button" onClick={() => setNotesModal(null)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
